import { useEffect, useMemo, useRef } from 'react'
import { cn, mergeRefs } from '../../../utils'
import { BaseField, ValidationIcon } from '../base-field/BaseField'
import { Input } from '../base-field/input'
import {
  autoformatDateValue,
  buildPattern,
  getDateSeparator,
  getFirstPartPattern,
} from './autoformatDateValue'
import { computeBackspaceBehavior, mapDigitsToCursorPosition } from './computeBackspaceBehavior'
import styles from './DateField.module.css'
import { DateFieldProps } from './DateField.types'

export function DateField(props: DateFieldProps) {
  const {
    ref,
    name,
    disabled,
    onBlur,
    onClick,
    placeholder,
    ariaLabel,
    status,
    value = '',
    onChange,
    className,
    dateFormat = 'dd.MM.yyyy',
    autoComplete = 'off',
    onKeyDown,
    inputMode,
    maxLength,
    pattern,
    formFieldClass,
    ...inputProps
  } = props

  const inputRef = useRef<HTMLInputElement | null>(null)
  const pendingCursorPositionRef = useRef<number | null>(null)
  const setInputRef = useMemo(() => mergeRefs<HTMLInputElement>(ref, inputRef), [ref])
  const separator = getDateSeparator(dateFormat)

  useEffect(() => {
    const cursorPosition = pendingCursorPositionRef.current

    if (cursorPosition === null || inputRef.current === null) {
      return
    }

    inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
    pendingCursorPositionRef.current = null
  }, [value])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const manuallyInsertedSeparator = (event.nativeEvent as InputEvent).data === separator
    const formattedValue = autoformatDateValue(event.currentTarget.value, dateFormat, {
      completePartWithInputSeparator: manuallyInsertedSeparator,
    })

    onChange?.(formattedValue)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' || !separator) {
      onKeyDown?.(event)
      return
    }

    const input = event.currentTarget
    const result = computeBackspaceBehavior({
      key: event.key,
      separator,
      selectionStart: input.selectionStart,
      selectionEnd: input.selectionEnd,
      inputValue: input.value,
      firstPartPattern: getFirstPartPattern(dateFormat),
    })

    if (!result.handled) {
      onKeyDown?.(event)
      return
    }

    event.preventDefault()
    const formattedValue = autoformatDateValue(result.nextRawValue, dateFormat, {
      completePartWithInputSeparator: false,
    })
    pendingCursorPositionRef.current = mapDigitsToCursorPosition(
      formattedValue,
      result.targetDigitsLeft,
      result.placeCursorAfterSeparator,
    )
    onChange?.(formattedValue)

    onKeyDown?.(event)
  }

  return (
    <BaseField {...props} className={cn(styles.dateField, className)} formFieldClass={formFieldClass}>
      <Input
        ref={setInputRef}
        id={name}
        name={name}
        disabled={disabled}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        onClick={onClick}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        type="text"
        inputMode={inputMode ?? 'numeric'}
        maxLength={maxLength ?? dateFormat.length}
        pattern={pattern ?? buildPattern(dateFormat)}
        autoComplete={autoComplete}
        {...inputProps}
      />
      <ValidationIcon status={status ?? 'default'} />
    </BaseField>
  )
}
